// geographycontent8.js
// Section Eight — Economic Activity and the Use of Natural Resources
// Grade9Ready Geography content module
//
// Covers:
// 103 Classification of Industry
// 105 Location of Industry
// 106 Changing Industry in the UK
// 108 The Use and Abuse of Resources
// 109 Energy and Power
// 110 Fishing and Mining
// 111 Deforestation and Conservation
// 113 Sustainable Development in Forests
// 114 Water Demand and Supply
// 119 Revision Summary for Section Eight

export const geographyContent8 = {
  id: "geography-section-8",
  subject: "Geography",
  sectionNumber: 8,
  title: "Economic Activity and the Use of Natural Resources",
  slug: "economic-activity-and-natural-resources",
  examBoardLink:
    "KS3 Geography: economic activity, industry sectors, natural resources, energy, sustainability and environmental management",
  intro:
    "This section explains how industries are classified, why industries locate in particular places, how the UK economy has changed, and how people use natural resources. It also explores energy, fishing, mining, deforestation, forest conservation, sustainable development and the challenge of meeting rising water demand.",
  keySkills: [
    "Classify economic activity into primary, secondary, tertiary and quaternary sectors.",
    "Explain why industries locate in particular places.",
    "Describe how employment structure changes over time.",
    "Explain the difference between renewable and non-renewable resources.",
    "Evaluate the environmental impacts of resource use.",
    "Explain how forests and water supplies can be managed sustainably.",
    "Use social, economic and environmental criteria in evaluation questions."
  ],

  topics: [
    {
      id: "geo8-1-classification-of-industry",
      title: "Classification of Industry",
      slug: "classification-of-industry",
      pageRef: "Source booklet p.103",
      learningObjectives: [
        "Classify industry into four economic sectors.",
        "Give examples of jobs in each sector.",
        "Explain how employment structure changes with development."
      ],
      keywords: [
        {
          term: "Primary industry",
          definition:
            "Economic activity that extracts or collects raw materials from the natural environment, such as farming, fishing, forestry and mining."
        },
        {
          term: "Secondary industry",
          definition:
            "Economic activity that manufactures or processes raw materials into products."
        },
        {
          term: "Tertiary industry",
          definition:
            "Economic activity that provides services, such as retail, healthcare, transport, tourism and education."
        },
        {
          term: "Quaternary industry",
          definition:
            "Economic activity based on knowledge, research, information technology and high-level services."
        },
        {
          term: "Employment structure",
          definition:
            "The proportion of workers employed in each sector of the economy."
        },
        {
          term: "Industrialisation",
          definition:
            "The growth of manufacturing industry within an economy."
        },
        {
          term: "Deindustrialisation",
          definition:
            "The decline of manufacturing industry in a country or region."
        }
      ],
      tutorial: [
        {
          heading: "The four sectors",
          body:
            "Industry is often divided into four sectors. Primary industries collect raw materials. Secondary industries make things from those materials. Tertiary industries provide services. Quaternary industries involve information, research, science, technology and knowledge-based work."
        },
        {
          heading: "Examples",
          body:
            "A farmer, miner or fisher works in the primary sector. A car factory worker or food processor works in the secondary sector. A nurse, teacher, shop assistant or train driver works in the tertiary sector. A software developer, researcher or data analyst works in the quaternary sector."
        },
        {
          heading: "Changing employment",
          body:
            "As countries develop, employment often shifts from primary work to secondary manufacturing, then towards tertiary and quaternary services. This does not mean primary or secondary work disappears, but a smaller proportion of people may work in those sectors."
        }
      ],
      workedExamples: [
        {
          question: "Classify the following jobs: coal miner, car factory worker, teacher, software engineer.",
          answer:
            "Coal miner = primary because coal is extracted from the ground. Car factory worker = secondary because raw materials and components are made into a product. Teacher = tertiary because education is a service. Software engineer = quaternary because the job is knowledge and technology based."
        },
        {
          question:
            "Explain why the tertiary sector often grows as a country develops.",
          answer:
            "As incomes rise, people spend more money on services such as healthcare, education, leisure, retail and transport. Better technology also reduces the number of workers needed in farming and manufacturing, so more people work in services."
        }
      ],
      commonMisconceptions: [
        "Primary industry does not mean simple or unskilled; it means extracting raw materials.",
        "Tertiary and quaternary are both service-based, but quaternary work is specifically knowledge, research and information focused.",
        "A product may pass through several sectors, such as wheat being grown, processed into bread, transported and sold."
      ],
      quickCheck: [
        {
          question: "Which job is in the primary sector?",
          options: ["Fisher", "Teacher", "Shop assistant", "Web designer"],
          answer: "Fisher",
          explanation:
            "Fishing extracts a natural resource from the environment, so it is primary industry."
        },
        {
          question: "Which sector includes research and information technology?",
          options: ["Primary", "Secondary", "Tertiary", "Quaternary"],
          answer: "Quaternary",
          explanation:
            "The quaternary sector is based on knowledge, data, research and technology."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how employment structure changes as countries develop.",
          marks: 6,
          indicativeContent:
            "In less developed economies, a high proportion of people may work in primary industries such as farming and mining. As industrialisation occurs, secondary manufacturing grows. Later, as incomes rise and technology improves, the tertiary and quaternary sectors expand because there is more demand for services, research, information and specialist jobs."
        }
      ],
      grade9Move:
        "Use a sequence: primary dominance → industrialisation and secondary growth → deindustrialisation or service growth → tertiary and quaternary expansion."
    },

    {
      id: "geo8-2-location-of-industry",
      title: "Location of Industry",
      slug: "location-of-industry",
      pageRef: "Source booklet p.105",
      learningObjectives: [
        "Identify factors affecting industrial location.",
        "Explain why different industries locate in different places.",
        "Distinguish between traditional and modern location factors."
      ],
      keywords: [
        {
          term: "Industrial location",
          definition:
            "The place where an industry or business chooses to operate."
        },
        {
          term: "Raw materials",
          definition:
            "Natural resources used to make products, such as timber, iron ore, oil or crops."
        },
        {
          term: "Market",
          definition:
            "The customers or area where goods and services are sold."
        },
        {
          term: "Labour",
          definition:
            "The workers available to an industry."
        },
        {
          term: "Transport links",
          definition:
            "Road, rail, port, airport and digital connections that allow goods, people and information to move."
        },
        {
          term: "Footloose industry",
          definition:
            "An industry that is not strongly tied to one particular location because it does not depend heavily on bulky raw materials."
        },
        {
          term: "Infrastructure",
          definition:
            "The basic systems needed for economic activity, such as roads, power, water, communications and transport."
        }
      ],
      tutorial: [
        {
          heading: "Traditional location factors",
          body:
            "Older heavy industries often located near raw materials, coalfields, rivers, ports and railways. This reduced transport costs when materials were bulky and expensive to move."
        },
        {
          heading: "Modern location factors",
          body:
            "Modern industries may be more influenced by skilled labour, motorway access, airports, broadband, universities, quality of life, government support and access to markets."
        },
        {
          heading: "Why location matters",
          body:
            "A good location can reduce costs, improve access to workers and customers, and make a business more competitive. A poor location can increase transport costs, limit labour supply and reduce profitability."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why a steelworks might traditionally locate near coal and iron ore.",
          answer:
            "Steel production uses bulky raw materials and large amounts of energy. Locating near coal and iron ore reduced transport costs and made production cheaper."
        },
        {
          question:
            "Explain why a high-tech company might locate near a university.",
          answer:
            "A university provides skilled graduates, research links and innovation. This helps high-tech firms recruit workers and develop new products."
        }
      ],
      commonMisconceptions: [
        "Not all industries need to be near raw materials anymore.",
        "Transport still matters, but digital communication and skilled labour are increasingly important for some industries.",
        "Footloose industries still need good infrastructure, even if they are not tied to raw materials."
      ],
      quickCheck: [
        {
          question:
            "Which factor was especially important for traditional heavy industry?",
          options: ["Nearby raw materials", "Online advertising", "Tourism beaches", "Population pyramids"],
          answer: "Nearby raw materials",
          explanation:
            "Heavy industries often needed bulky materials, so locating close to them reduced costs."
        },
        {
          question:
            "Which is most important for many modern high-tech industries?",
          options: ["Skilled labour", "Steep mountain slopes", "Poor broadband", "Isolation from markets"],
          answer: "Skilled labour",
          explanation:
            "High-tech industries often need educated and specialised workers."
        }
      ],
      examPractice: [
        {
          question:
            "Explain two factors that influence the location of industry.",
          marks: 4,
          indicativeContent:
            "Transport links influence location because businesses need to move workers, materials and products efficiently. Labour also matters because industries need workers with suitable skills. High-tech industries may locate near universities to access graduates and research."
        },
        {
          question:
            "Explain why the location factors for industry have changed over time.",
          marks: 6,
          indicativeContent:
            "Traditional heavy industry often located near raw materials and energy sources because these were bulky and costly to transport. Improvements in transport, container shipping, electricity supply and digital communication mean many modern industries are less tied to raw materials. Skilled labour, markets, broadband, airports and quality of life have become more important."
        }
      ],
      grade9Move:
        "Contrast old and new location factors. This shows change over time rather than just listing reasons."
    },

    {
      id: "geo8-3-changing-industry-in-the-uk",
      title: "Changing Industry in the UK",
      slug: "changing-industry-in-the-uk",
      pageRef: "Source booklet p.106",
      learningObjectives: [
        "Describe how UK industry has changed.",
        "Explain deindustrialisation and the growth of services.",
        "Identify impacts of industrial change on people and places."
      ],
      keywords: [
        {
          term: "Industrial Revolution",
          definition:
            "A period of rapid industrial growth, especially in manufacturing, coal, iron, textiles and transport."
        },
        {
          term: "Deindustrialisation",
          definition:
            "The decline of manufacturing industry and heavy industry."
        },
        {
          term: "Globalisation",
          definition:
            "The increasing connections between countries through trade, investment, technology, migration and culture."
        },
        {
          term: "Service sector",
          definition:
            "The part of the economy that provides services rather than making goods."
        },
        {
          term: "High-tech industry",
          definition:
            "Industry based on advanced technology, research and specialist skills."
        },
        {
          term: "Regeneration",
          definition:
            "The improvement or redevelopment of an area that has experienced decline."
        },
        {
          term: "Brownfield site",
          definition:
            "Land that has previously been built on and may be reused for new development."
        }
      ],
      tutorial: [
        {
          heading: "The UK as an industrial power",
          body:
            "The UK was one of the first countries to industrialise. Coal, iron, textiles, shipbuilding, railways and manufacturing were major employers, especially in towns and cities near coalfields, ports and transport routes."
        },
        {
          heading: "Deindustrialisation",
          body:
            "Many UK manufacturing and heavy industries declined because of cheaper overseas production, exhausted resources, automation, global competition and changing demand. This caused job losses in some industrial regions."
        },
        {
          heading: "Growth of services and high-tech work",
          body:
            "The UK economy now has a large tertiary and quaternary sector. Finance, education, healthcare, retail, tourism, creative industries, technology and research employ many people."
        },
        {
          heading: "Impacts of change",
          body:
            "Industrial change can create unemployment and derelict land, but regeneration can turn old industrial areas into housing, offices, parks, shops, cultural spaces and transport hubs."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain one reason for deindustrialisation in the UK.",
          answer:
            "Some manufacturing moved overseas where labour and production costs were lower. This made it harder for UK factories to compete, leading to closures and job losses."
        },
        {
          question:
            "How can regeneration help former industrial areas?",
          answer:
            "Regeneration can reuse derelict land for housing, shops, offices, transport and public spaces. This can create jobs, improve the environment and attract investment."
        }
      ],
      commonMisconceptions: [
        "Deindustrialisation does not mean the UK makes nothing; it means manufacturing employs a smaller share of workers than before.",
        "Industrial change can create both winners and losers.",
        "Regeneration can improve places, but it may also increase house prices and displace existing communities."
      ],
      quickCheck: [
        {
          question:
            "What does deindustrialisation mean?",
          options: [
            "Decline of manufacturing and heavy industry",
            "Growth of farming only",
            "Increase in birth rate",
            "Formation of glaciers"
          ],
          answer: "Decline of manufacturing and heavy industry",
          explanation:
            "Deindustrialisation is the reduction of industrial manufacturing activity and employment."
        },
        {
          question:
            "Which sector has grown strongly in the UK economy?",
          options: ["Tertiary services", "Only subsistence farming", "Only mining", "Only hunting"],
          answer: "Tertiary services",
          explanation:
            "The UK now has a large service-sector economy."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how and why industry in the UK has changed.",
          marks: 6,
          indicativeContent:
            "The UK used to have a large secondary sector based on coal, steel, textiles, shipbuilding and manufacturing. Deindustrialisation occurred because of cheaper overseas production, global competition, automation and changing demand. The tertiary and quaternary sectors have grown, with more jobs in finance, healthcare, education, retail, technology and research."
        },
        {
          question:
            "Assess the impacts of deindustrialisation on UK towns and cities.",
          marks: 8,
          indicativeContent:
            "Deindustrialisation caused job losses, unemployment, lower incomes and derelict industrial land in some areas. It also affected community identity where towns depended on one industry. However, regeneration has reused some brownfield sites for housing, offices, shops and leisure. New service and high-tech jobs can bring investment, but benefits may not reach everyone. Overall impact depends on how successfully areas adapt."
        }
      ],
      grade9Move:
        "Avoid saying the UK has ‘no industry’. Say the employment structure shifted from heavy manufacturing towards services and knowledge-based industries."
    },

    {
      id: "geo8-4-use-and-abuse-of-resources",
      title: "The Use and Abuse of Resources",
      slug: "use-and-abuse-of-resources",
      pageRef: "Source booklet p.108",
      learningObjectives: [
        "Define natural resources.",
        "Distinguish between renewable and non-renewable resources.",
        "Explain how resource use can create environmental damage."
      ],
      keywords: [
        {
          term: "Natural resource",
          definition:
            "Something from the natural environment that people use, such as water, soil, forests, fish, minerals and fossil fuels."
        },
        {
          term: "Renewable resource",
          definition:
            "A resource that can naturally replace itself if managed carefully, such as wind, solar energy, forests and fish stocks."
        },
        {
          term: "Non-renewable resource",
          definition:
            "A resource that cannot be replaced within a human timescale, such as coal, oil, gas and metal ores."
        },
        {
          term: "Overexploitation",
          definition:
            "Using a resource faster than it can recover."
        },
        {
          term: "Sustainability",
          definition:
            "Using resources in a way that meets present needs without preventing future generations from meeting theirs."
        },
        {
          term: "Environmental degradation",
          definition:
            "Damage to the environment, reducing its quality or ability to support life."
        }
      ],
      tutorial: [
        {
          heading: "Why resources matter",
          body:
            "People depend on natural resources for food, water, energy, shelter, transport, industry and trade. Economic activity relies on the effective use of resources."
        },
        {
          heading: "Renewable and non-renewable",
          body:
            "Renewable resources can be replaced naturally, but they can still be damaged by overuse. Non-renewable resources form over millions of years and are effectively finite on a human timescale."
        },
        {
          heading: "Use versus abuse",
          body:
            "Using resources becomes abuse when it causes serious long-term damage, waste or inequality. Examples include overfishing, deforestation, polluted rivers, soil erosion, mining damage and burning fossil fuels without controlling impacts."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why fish can be renewable but still become threatened.",
          answer:
            "Fish reproduce naturally, so they can be renewable. However, if fish are caught faster than they can reproduce, stocks decline and the resource becomes overexploited."
        },
        {
          question:
            "Why are fossil fuels non-renewable?",
          answer:
            "Coal, oil and gas take millions of years to form. Humans use them much faster than they are naturally replaced, so they are non-renewable."
        }
      ],
      commonMisconceptions: [
        "Renewable does not mean unlimited.",
        "Natural resources can be economic, social and environmental at the same time.",
        "Resource use can benefit people while still damaging ecosystems."
      ],
      quickCheck: [
        {
          question:
            "Which is a non-renewable resource?",
          options: ["Coal", "Wind", "Solar energy", "Managed woodland"],
          answer: "Coal",
          explanation:
            "Coal takes millions of years to form and cannot be replaced within a human timescale."
        },
        {
          question:
            "What does overexploitation mean?",
          options: [
            "Using a resource faster than it can recover",
            "Using no resources at all",
            "Only using renewable energy",
            "Protecting all ecosystems perfectly"
          ],
          answer: "Using a resource faster than it can recover",
          explanation:
            "Overexploitation causes resource decline and environmental damage."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how the use of natural resources can become unsustainable.",
          marks: 6,
          indicativeContent:
            "Resource use becomes unsustainable when resources are used faster than they can recover or when extraction causes long-term environmental damage. Overfishing can reduce fish stocks below recovery levels. Deforestation can destroy habitats and increase soil erosion. Burning fossil fuels uses finite resources and releases greenhouse gases, contributing to climate change."
        }
      ],
      grade9Move:
        "Use the phrase ‘rate of use compared with rate of replacement’. This helps explain sustainability clearly."
    },

    {
      id: "geo8-5-energy-and-power",
      title: "Energy and Power",
      slug: "energy-and-power",
      pageRef: "Source booklet p.109",
      learningObjectives: [
        "Identify different energy sources.",
        "Compare renewable and non-renewable energy.",
        "Evaluate the advantages and disadvantages of energy choices."
      ],
      keywords: [
        {
          term: "Energy source",
          definition:
            "A resource used to generate power, heat or movement."
        },
        {
          term: "Fossil fuels",
          definition:
            "Coal, oil and gas formed from ancient organic material over millions of years."
        },
        {
          term: "Renewable energy",
          definition:
            "Energy from sources that naturally replenish, such as wind, solar, hydroelectric, tidal, wave, geothermal and biomass."
        },
        {
          term: "Non-renewable energy",
          definition:
            "Energy from sources that will run out or cannot be replaced quickly, such as fossil fuels and uranium."
        },
        {
          term: "Energy mix",
          definition:
            "The combination of energy sources used by a country or region."
        },
        {
          term: "Hydroelectric power",
          definition:
            "Electricity generated using moving water."
        },
        {
          term: "Energy security",
          definition:
            "Having reliable and affordable access to enough energy."
        }
      ],
      tutorial: [
        {
          heading: "Non-renewable energy",
          body:
            "Coal, oil and gas have powered industry, transport and homes for many years. They are reliable and energy-rich, but they release greenhouse gases and air pollutants, and they will eventually run out."
        },
        {
          heading: "Renewable energy",
          body:
            "Renewable energy sources include wind, solar, hydroelectric, tidal, wave, geothermal and biomass. They produce less greenhouse gas during operation, but may be intermittent, expensive to build or limited by location."
        },
        {
          heading: "Choosing an energy mix",
          body:
            "Countries usually use a mix of energy sources. A good energy mix balances cost, reliability, environmental impact, availability and energy security."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain one advantage and one disadvantage of wind power.",
          answer:
            "Wind power is renewable and produces no direct greenhouse gas emissions during operation. However, it is intermittent because turbines only generate electricity when wind speeds are suitable."
        },
        {
          question:
            "Explain why fossil fuels are still widely used.",
          answer:
            "Fossil fuels are energy-rich, well-established and can provide reliable power. Many countries already have infrastructure for extracting, transporting and using them."
        }
      ],
      commonMisconceptions: [
        "Renewable energy is not impact-free; it can still affect landscapes, ecosystems and local communities.",
        "Solar and wind are renewable but intermittent.",
        "Energy security is not just about supply; affordability and reliability also matter."
      ],
      quickCheck: [
        {
          question:
            "Which is a renewable energy source?",
          options: ["Solar", "Coal", "Oil", "Natural gas"],
          answer: "Solar",
          explanation:
            "Solar energy is renewable because it comes from sunlight."
        },
        {
          question:
            "What is an energy mix?",
          options: [
            "The combination of energy sources used",
            "Only one power station",
            "The number of people in a country",
            "The total area of a forest"
          ],
          answer: "The combination of energy sources used",
          explanation:
            "Most countries rely on several energy sources rather than one."
        }
      ],
      examPractice: [
        {
          question:
            "Compare renewable and non-renewable energy sources.",
          marks: 6,
          indicativeContent:
            "Renewable energy sources such as wind, solar and hydroelectric power naturally replenish and produce lower emissions during operation. However, they may be intermittent, expensive to build or location-specific. Non-renewable sources such as coal, oil and gas are reliable and energy-rich, but they will run out and produce greenhouse gases and pollution."
        },
        {
          question:
            "Evaluate the importance of renewable energy for the future.",
          marks: 8,
          indicativeContent:
            "Renewable energy is important because it reduces reliance on finite fossil fuels and lowers greenhouse gas emissions. It can improve energy security where countries have local wind, solar or water resources. However, renewable energy can be intermittent and may require storage, grid upgrades and investment. Some schemes also affect landscapes or ecosystems. Overall, renewable energy is essential, but most countries need a balanced and reliable energy mix."
        }
      ],
      grade9Move:
        "Avoid simple ‘renewable good, fossil fuels bad’ answers. Evaluate reliability, cost, emissions, security and location."
    },

    {
      id: "geo8-6-fishing-and-mining",
      title: "Fishing and Mining",
      slug: "fishing-and-mining",
      pageRef: "Source booklet p.110",
      learningObjectives: [
        "Explain how fishing and mining use natural resources.",
        "Describe environmental impacts of fishing and mining.",
        "Evaluate ways these activities can be managed."
      ],
      keywords: [
        {
          term: "Fishing industry",
          definition:
            "Economic activity involving catching, processing and selling fish."
        },
        {
          term: "Overfishing",
          definition:
            "Catching fish faster than stocks can naturally replace themselves."
        },
        {
          term: "Bycatch",
          definition:
            "Unwanted marine animals caught accidentally during fishing."
        },
        {
          term: "Mining",
          definition:
            "The extraction of minerals, metals, coal or other materials from the Earth."
        },
        {
          term: "Quarrying",
          definition:
            "Extracting stone, sand or minerals from open pits at the surface."
        },
        {
          term: "Reclamation",
          definition:
            "Restoring land after it has been damaged by activities such as mining or quarrying."
        },
        {
          term: "Quota",
          definition:
            "A limit on how much of a resource, such as fish, can be taken."
        }
      ],
      tutorial: [
        {
          heading: "Fishing as a resource activity",
          body:
            "Fishing provides food, jobs and income. However, if too many fish are caught, stocks can collapse. Modern fishing vessels and technology can catch huge quantities quickly, increasing the risk of overfishing."
        },
        {
          heading: "Mining as a resource activity",
          body:
            "Mining provides materials for construction, energy, electronics, transport and industry. It can create jobs and export income, but it can also damage landscapes, pollute water and create waste."
        },
        {
          heading: "Managing impacts",
          body:
            "Fishing can be managed using quotas, protected areas, net-size rules and seasonal bans. Mining can be managed through environmental controls, safer waste storage, water treatment and land reclamation."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how overfishing can damage ecosystems.",
          answer:
            "Overfishing removes fish faster than they reproduce. This reduces fish stocks and can disrupt food chains because predators and prey depend on each other."
        },
        {
          question:
            "Explain one environmental impact of mining.",
          answer:
            "Mining can remove vegetation and soil, leaving scarred landscapes. Waste materials and chemicals can also pollute rivers and groundwater."
        }
      ],
      commonMisconceptions: [
        "Fish are renewable only if stocks are managed below recovery limits.",
        "Mining impacts continue after extraction unless land is restored.",
        "Economic benefits and environmental costs often occur together."
      ],
      quickCheck: [
        {
          question:
            "What is overfishing?",
          options: [
            "Catching fish faster than stocks can recover",
            "Protecting fish breeding grounds",
            "Only catching old fish",
            "Stopping all fishing permanently"
          ],
          answer: "Catching fish faster than stocks can recover",
          explanation:
            "Overfishing reduces stocks and can damage ecosystems."
        },
        {
          question:
            "What is reclamation in mining?",
          options: [
            "Restoring damaged land",
            "Catching unwanted fish",
            "Burning fossil fuels",
            "Exporting services"
          ],
          answer: "Restoring damaged land",
          explanation:
            "Reclamation improves or restores land after extraction."
        }
      ],
      examPractice: [
        {
          question:
            "Explain the social, economic and environmental impacts of mining.",
          marks: 6,
          indicativeContent:
            "Mining can provide jobs, income, export earnings and raw materials for industry. Socially, it may support communities but can also cause health risks or displacement. Environmentally, it can destroy habitats, scar landscapes, create waste and pollute water. The overall impact depends on management and regulation."
        },
        {
          question:
            "Evaluate strategies used to manage fishing sustainably.",
          marks: 8,
          indicativeContent:
            "Sustainable fishing strategies include catch quotas, protected marine areas, seasonal bans, net-size restrictions and monitoring. These can allow fish stocks to recover and protect breeding populations. However, they may reduce short-term income for fishers and require enforcement. If poorly enforced, illegal fishing can continue. Overall, sustainable fishing needs scientific monitoring, cooperation and effective regulation."
        }
      ],
      grade9Move:
        "For resource extraction, always balance economic benefit against environmental cost, then judge whether management reduces the damage."
    },

    {
      id: "geo8-7-deforestation-and-conservation",
      title: "Deforestation and Conservation",
      slug: "deforestation-and-conservation",
      pageRef: "Source booklet p.111",
      learningObjectives: [
        "Define deforestation and conservation.",
        "Explain causes and impacts of deforestation.",
        "Describe ways forests can be conserved."
      ],
      keywords: [
        {
          term: "Deforestation",
          definition:
            "The large-scale removal of forest."
        },
        {
          term: "Conservation",
          definition:
            "Protecting and managing the environment to prevent damage or loss."
        },
        {
          term: "Biodiversity",
          definition:
            "The variety of living organisms in an ecosystem."
        },
        {
          term: "Habitat loss",
          definition:
            "The destruction or reduction of the natural place where plants and animals live."
        },
        {
          term: "Soil erosion",
          definition:
            "The removal of soil by water, wind or human activity."
        },
        {
          term: "Carbon sink",
          definition:
            "A natural store that absorbs more carbon dioxide than it releases, such as forests."
        },
        {
          term: "Selective logging",
          definition:
            "Cutting down selected trees rather than clearing whole areas."
        }
      ],
      tutorial: [
        {
          heading: "Why forests are cleared",
          body:
            "Forests may be cleared for farming, cattle ranching, logging, mining, roads, settlements, dams and commercial plantations. These activities can create income and resources, but they also cause environmental damage."
        },
        {
          heading: "Impacts of deforestation",
          body:
            "Deforestation destroys habitats, reduces biodiversity, exposes soil to erosion, disrupts water cycles and releases carbon dioxide. It can also affect indigenous communities and reduce long-term resource security."
        },
        {
          heading: "Conservation",
          body:
            "Conservation aims to protect forests while allowing careful use. Strategies include national parks, protected areas, selective logging, replanting, education, ecotourism and stronger law enforcement."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain how deforestation can increase soil erosion.",
          answer:
            "Tree roots bind soil together and the canopy protects soil from heavy rain. When trees are removed, soil is exposed and more easily washed away by rainfall."
        },
        {
          question:
            "Explain how forests help regulate climate.",
          answer:
            "Forests absorb carbon dioxide through photosynthesis and store carbon in trees and soils. When forests are cleared or burned, carbon dioxide is released, contributing to climate change."
        }
      ],
      commonMisconceptions: [
        "Deforestation has economic causes as well as environmental impacts.",
        "Conservation does not always mean banning all use; it can mean careful management.",
        "Replanting trees helps, but it may not fully replace the biodiversity of an old-growth forest."
      ],
      quickCheck: [
        {
          question:
            "What is deforestation?",
          options: [
            "Large-scale removal of forest",
            "Planting new woodland",
            "Protecting national parks",
            "Sustainable fishing"
          ],
          answer: "Large-scale removal of forest",
          explanation:
            "Deforestation means clearing forest, often for farming, logging or development."
        },
        {
          question:
            "Which is an impact of deforestation?",
          options: [
            "Habitat loss",
            "Increased biodiversity in all cases",
            "Reduced soil erosion always",
            "No effect on climate"
          ],
          answer: "Habitat loss",
          explanation:
            "Removing forest destroys or fragments habitats."
        }
      ],
      examPractice: [
        {
          question:
            "Explain the causes and impacts of deforestation.",
          marks: 6,
          indicativeContent:
            "Deforestation is caused by farming, cattle ranching, logging, mining, road building and settlement growth. It can provide land, jobs and resources, but it causes habitat loss, reduced biodiversity, soil erosion and increased carbon dioxide emissions. It may also affect indigenous communities and disrupt local water cycles."
        },
        {
          question:
            "Evaluate conservation strategies used to protect forests.",
          marks: 8,
          indicativeContent:
            "Conservation strategies include protected areas, selective logging, replanting, ecotourism, education and law enforcement. These can protect biodiversity and support long-term resource use. However, enforcement can be difficult, local people may need income, and illegal logging may continue. Conservation is most successful when it balances environmental protection with local economic needs."
        }
      ],
      grade9Move:
        "Link trees to systems: carbon cycle, water cycle, nutrient cycle, soil protection and biodiversity."
    },

    {
      id: "geo8-8-sustainable-development-in-forests",
      title: "Sustainable Development in Forests",
      slug: "sustainable-development-in-forests",
      pageRef: "Source booklet p.113",
      learningObjectives: [
        "Define sustainable forest management.",
        "Explain how forests can be used without destroying them.",
        "Evaluate sustainable strategies in forest environments."
      ],
      keywords: [
        {
          term: "Sustainable forest management",
          definition:
            "Managing forests so they provide resources and income while protecting ecosystems for the future."
        },
        {
          term: "Ecotourism",
          definition:
            "Tourism that aims to protect the environment and benefit local people."
        },
        {
          term: "Agroforestry",
          definition:
            "Growing trees and crops together to support farming and forest protection."
        },
        {
          term: "Reforestation",
          definition:
            "Replanting trees in an area where forest has been removed."
        },
        {
          term: "Afforestation",
          definition:
            "Planting trees in an area that was not recently forested."
        },
        {
          term: "Indigenous people",
          definition:
            "Original or long-established communities with strong cultural links to an area."
        },
        {
          term: "Certification",
          definition:
            "A system showing that products, such as timber, meet agreed environmental or ethical standards."
        }
      ],
      tutorial: [
        {
          heading: "What sustainable forest use means",
          body:
            "Sustainable forest use means people can gain income, food, materials and services from forests without permanently destroying the ecosystem. It tries to balance people, profit and the environment."
        },
        {
          heading: "Sustainable strategies",
          body:
            "Strategies include selective logging, replanting, agroforestry, ecotourism, protected areas, forest certification and involving local communities in decisions."
        },
        {
          heading: "Why local involvement matters",
          body:
            "Local and indigenous communities often understand forest environments well and depend on them for livelihoods. Projects are more likely to succeed if local people benefit and have a role in management."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why selective logging is more sustainable than clear-felling.",
          answer:
            "Selective logging removes only selected trees, so much of the forest structure remains. This reduces habitat destruction, protects soil and allows the forest to recover more easily than clear-felling."
        },
        {
          question:
            "How can ecotourism support forest conservation?",
          answer:
            "Ecotourism can create income from protecting the forest rather than clearing it. Local people may gain jobs as guides, lodge workers or conservation staff, giving them a reason to protect the environment."
        }
      ],
      commonMisconceptions: [
        "Sustainable development is not the same as no development.",
        "Ecotourism must be carefully managed; too many visitors can still damage environments.",
        "Planting trees is useful, but monoculture plantations may not replace natural forest biodiversity."
      ],
      quickCheck: [
        {
          question:
            "Which is a sustainable forest management strategy?",
          options: [
            "Selective logging",
            "Clear-felling all trees",
            "Burning forest for short-term farming only",
            "Ignoring local communities"
          ],
          answer: "Selective logging",
          explanation:
            "Selective logging removes chosen trees while reducing wider forest damage."
        },
        {
          question:
            "What is ecotourism designed to do?",
          options: [
            "Protect the environment and benefit local people",
            "Destroy habitats for roads only",
            "Stop all visitors forever",
            "Remove all forest resources"
          ],
          answer: "Protect the environment and benefit local people",
          explanation:
            "Ecotourism links conservation with local economic benefit."
        }
      ],
      examPractice: [
        {
          question:
            "Explain how forests can be managed sustainably.",
          marks: 6,
          indicativeContent:
            "Forests can be managed sustainably through selective logging, replanting, protected areas, agroforestry and ecotourism. Selective logging reduces damage by removing only some trees. Replanting replaces trees that are cut down. Ecotourism creates income from conservation, while involving local people helps ensure projects meet community needs."
        },
        {
          question:
            "Evaluate the success of sustainable forest management strategies.",
          marks: 8,
          indicativeContent:
            "Sustainable forest management can protect biodiversity, reduce soil erosion, store carbon and provide income through selective logging, certification and ecotourism. However, success depends on enforcement, funding, local support and market demand for sustainable products. Illegal logging, poverty and pressure for farmland can undermine strategies. Overall, sustainable management is most effective when local communities benefit and rules are enforced."
        }
      ],
      grade9Move:
        "Use the sustainability triangle: environmental protection, economic income and social benefit for local people."
    },

    {
      id: "geo8-9-water-demand-and-supply",
      title: "Water Demand and Supply",
      slug: "water-demand-and-supply",
      pageRef: "Source booklet p.114",
      learningObjectives: [
        "Explain why water demand is increasing.",
        "Describe reasons for water surplus and water deficit.",
        "Evaluate ways water can be managed sustainably."
      ],
      keywords: [
        {
          term: "Water demand",
          definition:
            "The amount of water needed by people, farms, industry and services."
        },
        {
          term: "Water supply",
          definition:
            "The amount of water available for use."
        },
        {
          term: "Water surplus",
          definition:
            "A situation where water supply is greater than demand."
        },
        {
          term: "Water deficit",
          definition:
            "A situation where water demand is greater than supply."
        },
        {
          term: "Water stress",
          definition:
            "Pressure on water supplies when demand is high compared with availability."
        },
        {
          term: "Reservoir",
          definition:
            "An artificial lake used to store water."
        },
        {
          term: "Water transfer",
          definition:
            "Moving water from an area of surplus to an area of deficit."
        },
        {
          term: "Desalination",
          definition:
            "Removing salt from seawater to make it usable."
        }
      ],
      tutorial: [
        {
          heading: "Why water demand rises",
          body:
            "Water demand rises because of population growth, urbanisation, higher living standards, industry, farming and energy production. As people become wealthier, they often use more water through appliances, sanitation, food consumption and manufacturing."
        },
        {
          heading: "Why water supply varies",
          body:
            "Water supply depends on rainfall, climate, geology, rivers, aquifers, storage and infrastructure. Some areas have plenty of water, while others have seasonal drought or low rainfall."
        },
        {
          heading: "Water management",
          body:
            "Water can be managed using reservoirs, dams, water transfers, desalination, water meters, recycling, efficient irrigation, fixing leaks and public education. Each method has costs and benefits."
        }
      ],
      workedExamples: [
        {
          question:
            "Explain why urbanisation increases water demand.",
          answer:
            "Urbanisation increases the number of people living in towns and cities. More water is needed for homes, sanitation, schools, hospitals, businesses and industry."
        },
        {
          question:
            "Explain one advantage and one disadvantage of water transfer.",
          answer:
            "Water transfer can move water from areas of surplus to areas of deficit, improving supply. However, it is expensive and can damage ecosystems in the area where water is taken from."
        }
      ],
      commonMisconceptions: [
        "Water shortage is not only caused by lack of rainfall; demand, pollution, infrastructure and management matter too.",
        "A country may have enough water overall but still have shortages in some regions.",
        "Large water projects can solve supply problems but may create environmental and social costs."
      ],
      quickCheck: [
        {
          question:
            "What is water deficit?",
          options: [
            "Demand is greater than supply",
            "Supply is greater than demand",
            "No one uses water",
            "Only seawater is available worldwide"
          ],
          answer: "Demand is greater than supply",
          explanation:
            "Water deficit occurs when there is not enough available water to meet demand."
        },
        {
          question:
            "Which strategy reduces water demand?",
          options: [
            "Fixing leaks",
            "Increasing waste",
            "Polluting rivers",
            "Removing water meters"
          ],
          answer: "Fixing leaks",
          explanation:
            "Fixing leaks reduces wasted water and lowers demand on supply systems."
        }
      ],
      examPractice: [
        {
          question:
            "Explain why demand for water is increasing.",
          marks: 6,
          indicativeContent:
            "Water demand is increasing because the global population is growing, so more water is needed for drinking, sanitation and food production. Urbanisation increases demand from homes, schools, hospitals and industry. Rising living standards also increase water use through appliances, energy production, manufacturing and diets that require more irrigation."
        },
        {
          question:
            "Evaluate strategies used to manage water supply.",
          marks: 8,
          indicativeContent:
            "Reservoirs and dams can store water and provide reliable supply, but they are expensive and may flood land or disrupt ecosystems. Water transfer can move water from surplus to deficit areas, but it can damage donor regions and requires major infrastructure. Desalination provides water in coastal dry areas but is energy-intensive and costly. Demand management, such as fixing leaks, water meters and efficient irrigation, is often more sustainable but requires behaviour change and investment."
        }
      ],
      grade9Move:
        "Separate supply-side solutions from demand-side solutions. Then judge which is more sustainable in the long term."
    }
  ],

  sectionReview: {
    title:
      "Section Eight Review — Economic Activity and the Use of Natural Resources",
    summary:
      "Economic activity is divided into primary, secondary, tertiary and quaternary sectors. The location of industry depends on factors such as raw materials, labour, transport, markets, energy, infrastructure and technology. The UK economy has shifted from heavy industry towards services and knowledge-based work. Natural resources support human life and economic activity, but overuse can damage environments. Energy, fishing, mining, forests and water all require sustainable management.",
    mustKnow: [
      "Primary industry extracts raw materials.",
      "Secondary industry manufactures products.",
      "Tertiary industry provides services.",
      "Quaternary industry is based on knowledge, research and information.",
      "Traditional heavy industry often located near raw materials, energy and transport.",
      "Modern industry often values skilled labour, transport links, broadband and markets.",
      "The UK has experienced deindustrialisation and growth in services.",
      "Renewable resources can naturally replace themselves if managed carefully.",
      "Non-renewable resources cannot be replaced within a human timescale.",
      "Fossil fuels are reliable but finite and polluting.",
      "Renewable energy reduces emissions but can be intermittent or location-specific.",
      "Overfishing occurs when fish are caught faster than stocks recover.",
      "Mining creates economic benefits but can damage landscapes and water quality.",
      "Deforestation causes habitat loss, soil erosion, biodiversity loss and carbon emissions.",
      "Sustainable forest management balances environmental, social and economic needs.",
      "Water demand is rising because of population growth, urbanisation, farming, industry and higher living standards.",
      "Water can be managed through supply-side and demand-side strategies."
    ],
    highValueExamPhrases: [
      "Employment structure changes as economies develop...",
      "The location of industry depends on cost, access and efficiency...",
      "Traditional industries were tied to raw materials, whereas modern industries are often more footloose...",
      "This creates economic benefits but also environmental costs...",
      "Renewable does not mean unlimited; the rate of use must not exceed the rate of replacement...",
      "The strategy is more sustainable because it balances social, economic and environmental needs...",
      "Water scarcity can result from both limited supply and rising demand...",
      "A balanced energy mix improves energy security while reducing environmental impact..."
    ],
    mixedPractice: [
      {
        question:
          "Explain the difference between primary, secondary, tertiary and quaternary industry.",
        marks: 4,
        indicativeContent:
          "Primary industry extracts raw materials, such as farming, fishing and mining. Secondary industry manufactures products from raw materials. Tertiary industry provides services such as healthcare, transport and retail. Quaternary industry involves knowledge, research, data and information technology."
      },
      {
        question:
          "Explain why the UK economy has changed over time.",
        marks: 6,
        indicativeContent:
          "The UK economy has shifted from heavy manufacturing towards services and quaternary industries. Deindustrialisation occurred because of global competition, cheaper overseas production, automation and changing demand. Growth in healthcare, education, finance, retail, technology and research increased tertiary and quaternary employment."
      },
      {
        question:
          "Explain how resource use can damage the environment.",
        marks: 6,
        indicativeContent:
          "Resource use can damage the environment when resources are extracted or consumed unsustainably. Mining can destroy habitats, scar landscapes and pollute water. Overfishing reduces fish stocks and disrupts food chains. Deforestation causes habitat loss, soil erosion and carbon emissions. Burning fossil fuels releases greenhouse gases and air pollution."
      },
      {
        question:
          "Evaluate the use of renewable energy.",
        marks: 8,
        indicativeContent:
          "Renewable energy reduces reliance on finite fossil fuels and produces lower emissions during operation. Wind, solar and hydroelectric power can improve energy security where local resources are available. However, some renewables are intermittent, expensive to build, location-specific or visually controversial. They may also require storage and grid upgrades. Overall, renewables are important but need to be part of a reliable energy mix."
      },
      {
        question:
          "Assess whether forests can be used sustainably.",
        marks: 8,
        indicativeContent:
          "Forests can be used sustainably through selective logging, replanting, ecotourism, protected areas, certification and local community management. These strategies can provide income while protecting biodiversity, soil and carbon storage. However, illegal logging, poverty, demand for farmland and weak enforcement can undermine sustainability. Forest use is most sustainable when local people benefit and environmental rules are enforced."
      },
      {
        question:
          "Evaluate strategies used to manage water demand and supply.",
        marks: 8,
        indicativeContent:
          "Supply can be increased using reservoirs, dams, water transfers and desalination. These can improve reliability but are often expensive and may damage ecosystems or communities. Demand can be reduced through water meters, fixing leaks, recycling, efficient irrigation and public education. These are often more sustainable but require investment and behaviour change. The best approach usually combines supply management with demand reduction."
      }
    ]
  }
};

export default geographyContent8;